import { useState } from 'react';
import { Copy, CheckCircle, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRedeemPointsMutation } from '@/redux/points/points.api.slice';
import { IS_PROD_ENV } from '@/lib/env';

const REDEEM_MIN = IS_PROD_ENV ? 10 : 1;
const REDEEM_MAX = IS_PROD_ENV ? 500 : 50;

interface RedeemPanelProps {
  currentPoints: number;
  redemptionEligible: boolean;
}

export default function RedeemPanel({
  currentPoints,
  redemptionEligible,
}: RedeemPanelProps) {
  const [amount, setAmount] = useState(REDEEM_MIN);
  const [couponResult, setCouponResult] = useState<{
    couponCode: string;
    pointsRedeemed: number;
    expiresAt: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const [redeemPoints, { isLoading, error }] = useRedeemPointsMutation();

  const maxRedeemable = Math.min(currentPoints, REDEEM_MAX);
  const isValid =
    redemptionEligible &&
    Number.isInteger(amount) &&
    amount >= REDEEM_MIN &&
    amount <= maxRedeemable;

  async function handleRedeem() {
    try {
      const result = await redeemPoints({ amount }).unwrap();
      setCouponResult(result);
    } catch {
      // error shown inline via `error`
    }
  }

  function handleCopy() {
    if (!couponResult) return;
    navigator.clipboard.writeText(couponResult.couponCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const errorMessage =
    error && 'data' in error
      ? (error.data as { message?: string })?.message
      : null;

  return (
    <>
      {!redemptionEligible ? (
        <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          You need at least{' '}
          <span className="font-semibold text-foreground">
            {REDEEM_MIN} points
          </span>{' '}
          to redeem a coupon. Keep earning!
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="redeem-amount">Points to redeem</Label>
            <Input
              id="redeem-amount"
              type="number"
              min={REDEEM_MIN}
              max={maxRedeemable}
              value={amount}
              onChange={(e) => setAmount(Math.floor(Number(e.target.value)))}
              className="font-display text-lg tabular-nums"
            />
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-green-50 px-3 py-2.5">
              <dt className="text-xs text-green-900/70">Coupon value</dt>
              <dd className="font-display text-lg font-semibold tabular-nums text-green-800">
                ${amount} USD
              </dd>
            </div>
            <div className="rounded-lg bg-neutral-50 px-3 py-2.5">
              <dt className="text-xs text-muted-foreground">Remaining</dt>
              <dd className="font-display text-lg font-semibold tabular-nums">
                {Math.max(0, currentPoints - amount).toLocaleString()} pts
              </dd>
            </div>
          </dl>

          <p className="text-xs text-muted-foreground">
            Min {REDEEM_MIN} pts · Max {REDEEM_MAX} pts per 24 hrs · Coupon
            valid at{' '}
            <span className="font-medium text-foreground">
              onefinitycnc.com
            </span>
          </p>

          {errorMessage && (
            <p className="text-sm text-destructive-subtle">{errorMessage}</p>
          )}

          <Button
            onClick={handleRedeem}
            disabled={!isValid || isLoading}
            className="w-full"
          >
            {isLoading ? 'Processing…' : 'Redeem — Get Coupon Code'}
          </Button>
        </div>
      )}

      {/* Success dialog */}
      <Dialog open={!!couponResult} onOpenChange={() => setCouponResult(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-green-600" />
              Your Coupon Code
            </DialogTitle>
          </DialogHeader>
          {couponResult && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-4 flex items-center justify-between gap-3">
                <span className="font-mono text-lg font-bold tracking-widest text-foreground">
                  {couponResult.couponCode}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCopy}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>

              <div className="text-sm space-y-1 text-muted-foreground">
                <p>
                  Value:{' '}
                  <span className="font-semibold text-green-700 text-foreground">
                    ${couponResult.pointsRedeemed} USD
                  </span>
                </p>
                <p>
                  Expires:{' '}
                  <span className="text-foreground">
                    {format(new Date(couponResult.expiresAt), 'MMMM d, yyyy')}
                  </span>
                </p>
                <p className="pt-1 text-xs">
                  Use this code at{' '}
                  <a
                    href="https://www.onefinitycnc.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    onefinitycnc.com
                  </a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
